import type { JSX } from 'react';
import { Link, useLocation } from 'react-router-dom';

import type { Theme } from '@/types';
import { useT, useLang, pathFor, altPath, otherLang } from '@/i18n';
import './Navbar.css';

interface NavbarProps {
  theme: Theme;
  onToggleTheme: () => void;
}

export default function Navbar({ theme, onToggleTheme }: NavbarProps): JSX.Element {
  const t = useT();
  const lang = useLang();
  const { pathname } = useLocation();

  // Hors de l'accueil, une ancre nue (#expertise) resterait sur la page
  // courante : on prefixe alors avec le chemin de l'accueil, ce qui recharge
  // la page et laisse le navigateur scroller vers la section.
  const home = pathFor('/', lang);
  const onHome = pathname === home || pathname === `${home}/`;
  const anchor = (hash: string): string => (onHome ? hash : `${home}${hash}`);

  // Les ancres sont identiques dans les deux langues : ce sont les memes
  // sections, seul leur texte change.
  const links = [
    { href: '#expertise',   label: t.nav.expertise },
    { href: '#cnc',         label: t.nav.cnc       },
    { href: '#asterion',    label: t.nav.asterion  },
    { href: '#projets',     label: t.nav.projects  },
    { href: '#articles',    label: t.nav.articles  },
    { href: '#competences', label: t.nav.skills    },
    { href: '#parcours',    label: t.nav.timeline  },
  ];

  const target = otherLang(lang);

  return (
    <nav className="Navbar" aria-label={t.nav.ariaLabel}>
      <a href={onHome ? '#hero' : home} className="Navbar__logo" aria-label={t.nav.home}>
        <div className="Navbar__mark" aria-hidden="true">
          <div className="Navbar__mark-a" />
          <div className="Navbar__mark-b" />
          <div className="Navbar__mark-c" />
          <div className="Navbar__mark-dot" />
        </div>
        <span className="Navbar__name">Pierre Séré</span>
      </a>

      <ul className="Navbar__links" role="list">
        {links.map(({ href, label }) => (
          <li key={href}>
            <a href={anchor(href)} className="Navbar__link">{label}</a>
          </li>
        ))}
        <li>
          <a href={anchor('#contact')} className="Navbar__link Navbar__link--cta">{t.nav.contact}</a>
        </li>
      </ul>

      <div className="Navbar__right">
        <Link to={pathFor('/asterion', lang)} className="Navbar__link">{t.nav.founder}</Link>
        <a
          href="https://solutions-asterion.com"
          target="_blank"
          rel="noopener noreferrer"
          className="Navbar__asterion"
          aria-label="Asterion Solutions"
        >
          Asterion ↗
        </a>
        {/* Bascule de langue : elle mene a la MEME page dans l'autre langue,
            jamais a l'accueil. Aucune redirection automatique nulle part. */}
        <Link
          to={altPath(pathname, target)}
          className="Navbar__lang"
          hrefLang={target}
          aria-label={t.nav.switchLang}
        >
          {target.toUpperCase()}
        </Link>
        <button
          className="Navbar__theme"
          onClick={onToggleTheme}
          aria-label={theme === 'dark' ? t.nav.toLight : t.nav.toDark}
        >
          {theme === 'dark' ? '☀️' : '🌙'}
        </button>
      </div>
    </nav>
  );
}
